import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useCreateSite, useDeleteSite, useSites } from '@/lib/sites';
import { useClients } from '@/lib/clients';
import { useFormError } from '@/lib/form-error';
import type { SiteData } from '@/types/api';

type StatusFilter = '' | 'active' | 'paused' | 'archived';

const STATUS_TONE = {
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
} as const;

export function SitesIndex() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const sites = useSites({ q: q || undefined, status: status || undefined, page });
  const data = sites.data?.data;
  const meta = sites.data?.meta;
  const navigate = useNavigate();

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Globe}
        title="Websites"
        subtitle="Every client site you build or maintain. Blog content keys and form endpoints live here."
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> New site
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search name or domain…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as StatusFilter);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </Select>
      </div>

      {sites.isLoading ? (
        <SkeletonList rows={5} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Globe}
          title={q || status ? 'No sites match those filters' : 'No sites yet'}
          description={
            q || status
              ? 'Try a different search or clear the status filter.'
              : 'Add a site for a client to start managing their blog and form endpoints.'
          }
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> New site
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {data.map((site) => (
              <SiteRow key={site.id} site={site} onOpen={() => navigate(`/sites/${site.id}`)} />
            ))}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}

      <NewSiteDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function SiteRow({ site, onOpen }: { site: SiteData; onOpen: () => void }) {
  const remove = useDeleteSite();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <DataListRow onClick={onOpen} interactive>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{site.name}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          {site.client_name ? (
            <>
              <span className="truncate">{site.client_name}</span>
              <span aria-hidden>·</span>
            </>
          ) : null}
          <span className="truncate">{site.domain || 'No domain set'}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {site.posts_count ? (
          <span className="text-xs text-muted-foreground">
            {site.posts_count} {site.posts_count === 1 ? 'post' : 'posts'}
          </span>
        ) : null}
        {site.has_api_key ? <StatusPill tone="info">Keyed</StatusPill> : null}
        <StatusPill tone={STATUS_TONE[site.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
          {site.status}
        </StatusPill>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger
          onClick={(e) => e.stopPropagation()}
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100 aria-expanded:opacity-100"
          aria-label={`Actions for ${site.name}`}
        >
          <MoreHorizontal className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={onOpen}>Open</DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              const ok = await confirm({
                title: `Delete "${site.name}"?`,
                body: 'The site and its posts are archived, not destroyed. Its keys stop working immediately.',
                confirmLabel: 'Delete site',
                tone: 'danger',
              });
              if (ok) {
                await remove.mutateAsync(site.id);
                toast.success(`"${site.name}" deleted`);
              }
            }}
          >
            <Trash2 className="size-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </DataListRow>
  );
}

function NewSiteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const clients = useClients({ status: 'active' });
  const [clientId, setClientId] = useState('');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [pending, setPending] = useState(false);

  const create = useCreateSite(clientId ? Number(clientId) : undefined);
  const { error, wrap, clear } = useFormError();
  const navigate = useNavigate();
  const toast = useToast();

  function reset() {
    setClientId('');
    setName('');
    setDomain('');
    clear();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!clientId || pending) return;

    setPending(true);
    let created: SiteData | undefined;
    const ok = await wrap(async () => {
      created = await create.mutateAsync({ name, domain: domain || null });
    });
    setPending(false);

    if (ok && created) {
      toast.success(`"${created.name}" created`, 'Generate its site key from the Keys tab.');
      reset();
      onClose();
      navigate(`/sites/${created.id}`);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="New site"
      size="sm"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Client</span>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Pick a client…</option>
            {(clients.data?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Name</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Marketing site"
            required
          />
        </label>

        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Domain</span>
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
          />
          <span className="block text-xs text-[var(--color-foreground-muted)]">
            Optional. Used for display only — keys are what authenticate requests.
          </span>
        </label>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!clientId || !name || pending}>
            {pending ? 'Creating…' : 'Create site'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
